import React, {useEffect, useState} from "react";
import Typography from "@mui/material/Typography";

import Stack from "@mui/material/Stack";
import {useFormikContext} from "formik";
import {BtnAdd} from "./BtnAdd";
import {BtnDelete} from "./BtnDelete";
import {BtnDeleteCopyrightType} from "./BtnDeleteCopyrightType";
import {SHOW_ADD_COPYRIGHT_CLAUSE_FIELD, SHOW_ADD_LICENSE_FIELD, useCopyrightState} from "../copyrightContext";
import {LicenseFieldsWrapper} from "../LicenseFieldsWrapper";
import {CopyrightClauseWrapper} from "../CopyrightClauseWrapper";
import {CopyrightTypeFieldsWrapper} from "../CopyrightTypeFieldsWrapper";
import {FormikTextField} from "../../form/FormikTextField";

/**
 * A form component for the copyright section of the form.
 *
 * Displays the existing licenses, copyright clauses, and copyright type, as well as
 * fields for adding new licenses and clauses.
 *
 * @param {Object} props The component props.
 * @param {function} props.t The translation function.
 * @param {string} props.collectionId The ID of the collection.
 * @param {boolean} props.fileUploadMode Whether the component is in file upload mode.
 * @param {string} props.maxWidth The maximum width of the component in pixels, e.g. '100px'.
 * @param {boolean} props.active Whether the component is active.
 * @returns {JSX.Element} The rendered component.
 */
export const CopyrightSectionWrapper = ({t, collectionId, fileUploadMode, maxWidth, active = true}) => {
    const {showAddLicenseField, showAddCopyrightClause} = useCopyrightState();
    const {values} = useFormikContext();

    const [data, setData] = useState({});

    useEffect(() => {
        if (!values) {
            return;
        }
        setData(values);
    }, [values]);

    /**
     * Displays the form fields related to the existing licenses.
     * @returns {*}
     */
    const getExistingLicenses = () => {
        return (
            <><Typography variant="h6">{t('headingLicenses', 'Lisenser')}</Typography>
                {data['licenses']?.map((_l, ix) => {
                    const itemKey = _l.reference.id;
                    return (
                        <Stack
                            key={`lic-${itemKey}`}
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                marginBottom: 1.5,
                                width: '100%'
                            }}>
                            <LicenseFieldsWrapper key={`licenseWrapper-${itemKey}`}
                                                  t={t}
                                                  ix={ix}/>
                            <BtnDelete objectType={'license'} object={_l} fileUploadMode={fileUploadMode} ix={ix}/>
                        </Stack>
                    );
                })}
            </>
        );
    };

    /**
     * Displays the form fields related to the existing copyright clauses.
     * @returns {JSX.Element}
     */
    function getExistingCopyrightClauses() {
        const copyrightInfo = data['copyrightInfo'];
        return copyrightInfo && <>
            <Typography variant="h6">{t('headingCopyrightClause', 'Klausul')}</Typography>
            {copyrightInfo.length > 0 && (
                copyrightInfo?.filter(c => c && !c.deleted === true).map((_c, ix) => {
                    const itemKey = _c.sourceId;
                    return (
                        <Stack
                            key={`copy-${itemKey}`}
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                marginBottom: 1.5,
                                width: '100%'
                            }}>
                            <CopyrightClauseWrapper key={`copyrightClauseWrapper-${itemKey}`}
                                                    t={t}
                                                    ix={ix}
                                                    collectionId={collectionId}
                            />

                            <BtnDelete objectType={'clause'} object={_c} fileUploadMode={fileUploadMode} ix={ix}
                                       values={values}/>
                        </Stack>
                    );
                }))
            }
        </>;
    }

    /**
     * Displays the copyright type.
     * @returns {JSX.Element}
     */
    function getCopyrightType() {
        return (
            <>
                <Typography variant="h6" gutterBottom>{t('headingCopyrightType', 'Opphavsrett')}</Typography>
                <Stack
                    key={`ctype`}
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        marginBottom: 1.5,
                        width: '100%'
                    }}>
                    <CopyrightTypeFieldsWrapper t={t}/>
                    <BtnDeleteCopyrightType fileUploadMode={fileUploadMode}/>
                </Stack>
            </>
        );
    }

    return active && data && (<>
            <Stack
                sx={{
                    alignItems: 'flex-start',
                    width: '100%',
                    maxWidth: maxWidth
                }}>
                <Typography variant="h5"
                            sx={{marginBottom: 2}}>{t('headingRights', 'Rettigheter')}</Typography>

                {/* Copyright type */}
                {getCopyrightType()}

                {/* Licenses */}
                {getExistingLicenses()}
                {showAddLicenseField && <LicenseFieldsWrapper key={'noLicenses'}
                                                              t={t}
                                                              ix={values.licenses?.length || 0}/>
                }
                <BtnAdd label={t('btnAddNewLicense', 'Legg til nytt lisensfelt')}
                        dispatchValue={SHOW_ADD_LICENSE_FIELD}/>

                {/* Clauses */}
                {getExistingCopyrightClauses()}

                {showAddCopyrightClause &&
                    <CopyrightClauseWrapper key={'noCopyrightClauses'}
                                            t={t}
                                            ix={values.copyrightInfo?.length || 0}
                                            collectionId={collectionId}/>
                }

                <BtnAdd label={t('btnAddCopyrightClause', 'Legg til klausuler')}
                        dispatchValue={SHOW_ADD_COPYRIGHT_CLAUSE_FIELD}/>
            </Stack>

            <FormikTextField formikKey={'copyrightTerms'}
                             label={t('copyrightTerms', 'Rettighetsbetingelser')}
                             helperText={t('copyrightTermsHelpText', 'Betingelser som gjelder rettighetene for objektet')}
                             fullWidth={true}
                             multiline={true}
                             size={"small"}/>
        </>
    );
};